/**
 * StageQuoteBar — agreed quote for the next piece of work (3 Nov 2026).
 *
 * Vanessa: "we need users to insert an agreed quote for the next piece of
 * work and the moneybar to alert at 75% of the quote being reached in WIP".
 *
 * Shows time used against the current agreed quote, with a notch at 75%.
 * At 75% the bar and a banner turn amber; past 100% they turn red. Any
 * member of staff can record a new quote, which replaces the last one.
 * Backed by GET/POST /api/matters/{ref}/stage-quote.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { AlertTriangle, PencilLine, Plus, Loader2 } from "lucide-react";

function fmt(n) {
  if (n == null || isNaN(n)) return "£0";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function StageQuoteBar({ matterRef, stage, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [agreedOn, setAgreedOn] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const open = () => {
    setAmount("");
    setDescription("");
    setAgreedOn(todayIso());
    setErr("");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const val = parseFloat(String(amount).replace(/[£,]/g, ""));
    if (isNaN(val) || val <= 0) { setErr("Enter the agreed amount"); return; }
    if (!description.trim()) { setErr("Say what the quote covers"); return; }
    setSaving(true);
    setErr("");
    try {
      await axios.post(`${API}/matters/${encodeURIComponent(matterRef)}/stage-quote`, {
        amount: val, description: description.trim(), agreed_on: agreedOn,
      });
      setEditing(false);
      if (onSaved) onSaved();
    } catch (ex) {
      setErr(errorMessage(ex, "Could not save the agreed quote"));
    }
    setSaving(false);
  };

  const form = editing && (
    <form onSubmit={save} className="mt-2 flex flex-wrap items-end gap-2 text-xs" data-testid="stage-quote-form">
      <label className="flex flex-col gap-1">
        <span className="text-txt-muted">Agreed quote (£, ex VAT)</span>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
          className="w-28 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-400"
          data-testid="stage-quote-amount" autoFocus />
      </label>
      <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
        <span className="text-txt-muted">What it covers</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
          placeholder="e.g. Draft and serve the defence"
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-400"
          data-testid="stage-quote-description" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-txt-muted">Agreed on</span>
        <input type="date" value={agreedOn} max={todayIso()} onChange={(e) => setAgreedOn(e.target.value)}
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-400"
          data-testid="stage-quote-date" />
      </label>
      <button type="submit" disabled={saving}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60"
        data-testid="stage-quote-save">
        {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
      </button>
      <button type="button" onClick={() => setEditing(false)}
        className="px-3 py-1.5 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-800">
        Cancel
      </button>
      {err && <p className="w-full text-status-red" data-testid="stage-quote-error">{err}</p>}
    </form>
  );

  if (!stage) {
    return (
      <div className="mt-3" data-testid="stage-quote-none">
        {!editing && (
          <button onClick={open}
            className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200">
            <Plus className="w-3.5 h-3.5" /> Add the agreed quote for the next piece of work
          </button>
        )}
        {form}
      </div>
    );
  }

  const pct = stage.percent_used || 0;
  const fill = Math.min(pct, 100);
  const threshold = stage.alert_threshold_percent || 75;
  const barColour = stage.over ? "bg-status-red" : stage.alert ? "bg-status-amber" : "bg-indigo-500";

  return (
    <div className="mt-3" data-testid="stage-quote-bar">
      <div className="flex items-end justify-between mb-1 text-[10px] gap-2">
        <span className="text-slate-300 truncate" title={stage.description}>
          Next piece of work: <span className="text-white">{stage.description}</span>
        </span>
        <span className="text-txt-muted whitespace-nowrap flex items-center gap-1">
          {fmt(stage.spent)} of {fmt(stage.amount)} ({Math.round(pct)}%)
          {!editing && (
            <button onClick={open} title="Record a new agreed quote" className="opacity-60 hover:opacity-100"
              data-testid="stage-quote-new">
              <PencilLine className="w-3 h-3" />
            </button>
          )}
        </span>
      </div>
      <div className="relative w-full h-2 bg-slate-800 rounded-full">
        <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${barColour}`}
          style={{ width: `${fill}%` }} data-testid="stage-quote-fill" />
        <div className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-white/70 rounded-full"
          style={{ left: `${threshold}%` }} title={`${threshold}% warning`} />
      </div>
      {stage.alert && (
        <p className={`mt-1.5 flex items-center gap-1.5 text-xs ${stage.over ? "text-status-red" : "text-status-amber"}`}
          data-testid="stage-quote-alert">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {stage.over
            ? `Over the agreed quote by ${fmt(Number(stage.spent) - Number(stage.amount))}. Speak to the client and agree a revised quote.`
            : `${Math.round(pct)}% of the agreed quote used. Speak to the client before it is exceeded.`}
        </p>
      )}
      {form}
    </div>
  );
}
